import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useTenant } from "../context/TenantContext";
import "../styles/UpdatePrompt.css";
import logger from '../utils/logger';

const inputStyle = {
  width: "100%", padding: "12px 14px", borderRadius: "8px",
  border: "1px solid #ccc", fontSize: "15px", boxSizing: "border-box",
};

// This clinic's own GHL location and Private Integration key. Patients of this
// clinic are only ever sent to the location saved here.
const UpdateGhlConnection = () => {
  const { tenant } = useTenant();
  const tenantId = tenant?.id || window.__TENANT_ID__ || localStorage.getItem("superadmin_tenant_id");

  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [locationId, setLocationId] = useState("");
  const [checked, setChecked] = useState(null); // { locationName, pdfFields, warning } for the current inputs
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ text: "", ok: false });

  useEffect(() => {
    if (tenantId) fetchStatus();
  }, [tenantId]);

  const fetchStatus = async () => {
    try {
      const res = await api.get(`/api/admin/ghl-config/${tenantId}`);
      setStatus(res.data);
      setLocationId(res.data.locationId || "");
    } catch (error) {
      logger.error("Error fetching GHL connection:", error);
      setStatus({ configured: false });
    }
  };

  // Any change to the key or location means the check has to run again.
  const onKeyChange = (value) => { setApiKey(value); setChecked(null); };
  const onLocationChange = (value) => { setLocationId(value); setChecked(null); };

  const handleCheck = async () => {
    setBusy("check");
    setMessage({ text: "", ok: false });
    try {
      const res = await api.post(`/api/admin/ghl-config/${tenantId}/verify`, { apiKey: apiKey.trim(), locationId: locationId.trim() });
      setChecked(res.data);
    } catch (err) {
      setMessage({ text: err.response?.data?.message || err.message, ok: false });
    } finally {
      setBusy("");
    }
  };

  const handleSave = async () => {
    setBusy("save");
    setMessage({ text: "", ok: false });
    try {
      const res = await api.put(`/api/admin/ghl-config/${tenantId}`, {
        apiKey: apiKey.trim(), locationId: locationId.trim(),
      });
      setMessage({ text: `Connected to ${res.data.locationName || res.data.locationId}.`, ok: true });
      setApiKey("");
      setShowKey(false);
      setChecked(null);
      await fetchStatus();
    } catch (err) {
      setMessage({ text: err.response?.data?.message || err.message, ok: false });
    } finally {
      setBusy("");
    }
  };

  const handleCreatePdfFields = async () => {
    setBusy("pdf");
    setMessage({ text: "", ok: false });
    try {
      const res = await api.post(`/api/admin/ghl-config/${tenantId}/pdf-fields`);
      setStatus((prev) => ({ ...prev, pdfFields: res.data.pdfFields }));
      setMessage({ text: res.data.created.length ? `Created in GHL: ${res.data.created.join(", ")}.` : "Both PDF fields already exist.", ok: true });
    } catch (err) {
      setMessage({ text: err.response?.data?.message || err.message, ok: false });
    } finally {
      setBusy("");
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Disconnect this clinic from GHL? Intake will fall back to this clinic's webhooks.")) return;
    setBusy("remove");
    setMessage({ text: "", ok: false });
    try {
      await api.delete(`/api/admin/ghl-config/${tenantId}`);
      setMessage({ text: "Disconnected from GHL.", ok: true });
      setApiKey("");
      setChecked(null);
      await fetchStatus();
    } catch (err) {
      setMessage({ text: err.response?.data?.message || err.message, ok: false });
    } finally {
      setBusy("");
    }
  };

  if (!status) {
    return (
      <div className="update-container">
        <h2>Loading configuration...</h2>
      </div>
    );
  }

  const canCheck = locationId.trim() && (apiKey.trim() || status.configured);
  // PDFs go into the location's own "Intake/Follow-up Patient Merged PDF" file fields, found by name.
  const pdfFieldList = (pdfFields) => (
    <ul style={{ margin: "6px 0 0 0", paddingLeft: "20px", fontSize: "13px", color: "#374151" }}>
      {["intake", "followup"].map((formType) => {
        const field = pdfFields?.[formType];
        if (!field) return null;
        return (
          <li key={formType}>
            {field.found ? "\u2705" : "\u26A0\uFE0F"} {field.expectedName}: {field.found ? "found" : "missing, so these PDFs won't upload"}
          </li>
        );
      })}
    </ul>
  );
  const missingPdfFields = status.pdfFields && Object.values(status.pdfFields).some((f) => !f.found);

  return (
    <div className="update-container">
      <h2>GHL Connection</h2>

      {/* Status */}
      <div style={{
        padding: "16px 20px", borderRadius: "8px", marginBottom: "24px",
        backgroundColor: status.configured ? "#f0fdf4" : "#fef2f2",
        border: `1px solid ${status.configured ? "#86efac" : "#fca5a5"}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontSize: "20px" }}>{status.configured ? "✅" : "❌"}</span>
          <strong style={{ color: status.configured ? "#166534" : "#991b1b" }}>
            {status.configured ? `Connected to ${status.locationName || status.locationId}` : "Not connected to GHL"}
          </strong>
        </div>
        {status.configured && (
          <div style={{ fontSize: "14px", color: "#374151" }}>
            Location <code>{status.locationId}</code> · Key <code style={{ backgroundColor: "#f3f4f6", padding: "2px 8px", borderRadius: "4px" }}>{status.maskedKey}</code>
            {pdfFieldList(status.pdfFields)}
            {missingPdfFields && (
              <button onClick={handleCreatePdfFields} disabled={!!busy} style={{ marginTop: "10px" }}>
                {busy === "pdf" ? "Creating..." : "Create missing PDF fields in GHL"}
              </button>
            )}
          </div>
        )}
        {status.keyUnreadable && (
          <p style={{ fontSize: "13px", color: "#991b1b", margin: 0 }}>
            A key is saved but can't be read (the server's encryption secret may have changed). Enter the key again.
          </p>
        )}
        {!status.configured && !status.keyUnreadable && (
          <p style={{ fontSize: "13px", color: "#991b1b", margin: 0 }}>
            This clinic's intake isn't sent to GHL through the API. Steps fall back to this clinic's webhooks.
          </p>
        )}
      </div>

      <div className="prompt-section">
        <label>GHL Location ID</label>
        <input type="text" value={locationId} onChange={(e) => onLocationChange(e.target.value)}
          placeholder="Settings → Business Profile → Location ID" style={{ ...inputStyle, fontFamily: "monospace" }} />

        <label style={{ display: "block", fontWeight: "600", marginTop: "16px", marginBottom: "4px" }}>
          {status.configured ? "Private Integration key (leave blank to keep the saved key)" : "Private Integration key"}
        </label>
        <div style={{ position: "relative" }}>
          <input
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => onKeyChange(e.target.value)}
            placeholder="pit-..."
            autoComplete="off"
            style={{ ...inputStyle, padding: "12px 50px 12px 14px", fontFamily: "monospace" }}
          />
          <button type="button" onClick={() => setShowKey(!showKey)}
            style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#6b7280", padding: "4px" }}>
            {showKey ? "Hide" : "Show"}
          </button>
        </div>
        <p style={{ fontSize: "13px", color: "#6b7280", margin: "8px 0 0 0" }}>
          Create it in this clinic's own GHL sub-account under Settings → Private Integrations, with contacts, custom fields and locations access.
        </p>

        {checked && (
          <div style={{ marginTop: "16px", padding: "12px 14px", borderRadius: "8px", backgroundColor: "#f0fdf4", border: "1px solid #86efac" }}>
            <strong style={{ color: "#166534" }}>Key works for: {checked.locationName || locationId}</strong>
            <div style={{ fontSize: "13px", color: "#374151", marginTop: "4px" }}>
              Make sure this is {tenant?.brandName || tenant?.name || "this clinic"}'s own location before saving.
            </div>
            {checked.warning && <div style={{ fontSize: "13px", color: "#991b1b", marginTop: "6px" }}>{checked.warning}</div>}
            {pdfFieldList(checked.pdfFields)}
          </div>
        )}

        <div style={{ display: "flex", gap: "12px", marginTop: "18px" }}>
          {!checked ? (
            <button onClick={handleCheck} disabled={!canCheck || !!busy} style={{ flex: 1 }}>
              {busy === "check" ? "Checking..." : "Check connection"}
            </button>
          ) : (
            <button onClick={handleSave} disabled={!!busy || !!checked.warning} style={{ flex: 1 }}>
              {busy === "save" ? "Saving..." : "Save connection"}
            </button>
          )}
          {status.configured && (
            <button onClick={handleRemove} disabled={!!busy}
              style={{ padding: "10px 20px", background: busy ? "#ccc" : "#dc2626", color: "white",
                border: "none", borderRadius: "8px", fontSize: "15px", cursor: busy ? "not-allowed" : "pointer" }}>
              {busy === "remove" ? "Disconnecting..." : "Disconnect"}
            </button>
          )}
        </div>

        {message.text && (
          <p className={`status-message ${message.ok ? "success" : "error"}`}
            style={{ marginTop: "14px", padding: "10px", borderRadius: "6px",
              backgroundColor: message.ok ? "#f0fdf4" : "#fef2f2", color: message.ok ? "#166534" : "#991b1b" }}>
            {message.text}
          </p>
        )}
      </div>

      <div style={{
        padding: "16px 20px", borderRadius: "8px", marginTop: "24px",
        backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
        fontSize: "13px", color: "#1e40af", lineHeight: "1.6",
      }}>
        <strong>How it works:</strong>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li>Each clinic sends its patients only to its own GHL location; there is no shared key.</li>
          <li>A location already connected to another clinic can't be saved here.</li>
          <li>The key is checked against GHL, then encrypted before storage. It is never shown again.</li>
          <li>Intake and follow-up PDFs go into this location's "Intake Patient Merged PDF" and "Follow-up Patient Merged PDF" file fields, found by name. Missing ones can be created here.</li>
          <li>Without a connection, intake steps use this clinic's webhooks instead.</li>
        </ul>
      </div>
    </div>
  );
};

export default UpdateGhlConnection;
